import React, { useEffect, useState } from 'react';
import { UserPlus, Save, Shield, RefreshCcw, Trash2, SlidersHorizontal, Home } from 'lucide-react';
import authFetch from '../../utils/authFetch';
import './UserManagementPage.css';

const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';

const permissionLabels = {
  canDashboard: 'Dashboard',
  canRealEstate: 'Real Estate',
  canRealEstateHub: 'Real Estate Hub',
  canLeadGen: 'Lead Gen',
  canCheckedAreas: 'Checked Areas Map',
  canContacts: 'Contacts',
  canPropertiesMap: 'Properties Map',
  canLandMap: 'Land Map',
  canRawData: 'Raw Data',
  canMasterPortal: 'Master Portal',
  canShortTermRentals: 'Short Term Rentals',
  canShortTermRentalsFinancials: 'Short Term Rentals: Financials',
  canShortTermRentalsCleaning: 'Short Term Rentals: Cleaning & Maintenance',
  canShortTermRentalsCleaningEdit: 'Cleaning: Edit Checklists',
  canDocumentation: 'Documentation',
  canMyTime: 'My Time',
  canTimeTracking: 'Time Tracking',
  canTimeTrackingOverview: 'Time Tracking Overview',
  canAdmin: 'Admin',
  canAdminDbBackup: 'Admin DB Backup',
  canUserManagement: 'User Management'
};

const permissionSections = [
  {
    key: 'canDashboard',
    label: permissionLabels.canDashboard,
    children: []
  },
  {
    key: 'canRealEstate',
    label: permissionLabels.canRealEstate,
    children: [
      'canRealEstateHub',
      'canLeadGen',
      'canCheckedAreas',
      'canContacts',
      'canPropertiesMap',
      'canLandMap',
      'canRawData',
      'canMasterPortal'
    ]
  },
  {
    key: 'canShortTermRentals',
    label: permissionLabels.canShortTermRentals,
    children: ['canShortTermRentalsFinancials', 'canShortTermRentalsCleaning', 'canShortTermRentalsCleaningEdit']
  },
  {
    key: 'canDocumentation',
    label: permissionLabels.canDocumentation,
    children: []
  },
  {
    key: 'canMyTime',
    label: permissionLabels.canMyTime,
    children: ['canTimeTracking', 'canTimeTrackingOverview']
  },
  {
    key: 'canAdmin',
    label: permissionLabels.canAdmin,
    children: ['canAdminDbBackup', 'canUserManagement']
  }
];

const mainPageOptions = [
  { value: '/', label: 'Dashboard', group: 'Dashboard' },
  { value: '/real-estate', label: 'Real Estate Hub', group: 'Real Estate' },
  { value: '/real-estate/lead-gen', label: 'Lead Gen', group: 'Real Estate' },
  { value: '/real-estate/lead-gen/checked-areas', label: 'Checked Areas Map', group: 'Real Estate' },
  { value: '/real-estate/contacts', label: 'Contacts', group: 'Real Estate' },
  { value: '/real-estate/properties-map', label: 'Properties Map', group: 'Real Estate' },
  { value: '/real-estate/land-map', label: 'Land Map', group: 'Real Estate' },
  { value: '/real-estate/raw', label: 'Raw Data', group: 'Real Estate' },
  { value: '/real-estate/master-portal', label: 'Master Portal', group: 'Real Estate' },
  { value: '/short-term-rentals', label: 'Short Term Rentals Hub', group: 'Short Term Rentals' },
  { value: '/short-term-rentals/financials', label: 'Financials', group: 'Short Term Rentals' },
  { value: '/short-term-rentals/cleaning', label: 'Cleaning & Maintenance', group: 'Short Term Rentals' },
  { value: '/documentation', label: 'Documentation', group: 'Documentation' },
  { value: '/my-time', label: 'My Time Hub', group: 'My Time & Tasks' },
  { value: '/my-time/activity-log', label: 'Activity Log', group: 'My Time & Tasks' },
  { value: '/my-time/time-tracking', label: 'Time Tracking', group: 'My Time & Tasks' },
  { value: '/my-time/time-tracking/all', label: 'Time Tracking Overview', group: 'My Time & Tasks' },
  { value: '/tasks', label: 'Tasks', group: 'My Time & Tasks' },
  { value: '/admin', label: 'Admin Dashboard', group: 'Admin' },
  { value: '/admin/db-backup', label: 'Admin DB Backup', group: 'Admin' },
  { value: '/admin/users', label: 'User Management', group: 'Admin' }
];

const groupedMainPageOptions = mainPageOptions.reduce((acc, option) => {
  if (!acc[option.group]) {
    acc[option.group] = [];
  }
  acc[option.group].push(option);
  return acc;
}, {});

const childPermissionMap = permissionSections.reduce((accumulator, section) => {
  accumulator[section.key] = section.children;
  return accumulator;
}, {});

const parentPermissionMap = permissionSections.reduce((accumulator, section) => {
  section.children.forEach((childKey) => {
    accumulator[childKey] = section.key;
  });
  return accumulator;
}, {});

const defaultPermissions = {
  canDashboard: true,
  canRealEstate: true,
  canRealEstateHub: true,
  canLeadGen: true,
  canCheckedAreas: true,
  canContacts: true,
  canPropertiesMap: true,
  canLandMap: true,
  canRawData: true,
  canMasterPortal: true,
  canShortTermRentals: true,
  canShortTermRentalsFinancials: true,
  canShortTermRentalsCleaning: true,
  canShortTermRentalsCleaningEdit: false,
  canDocumentation: false,
  canMyTime: false,
  canTimeTracking: false,
  canTimeTrackingOverview: false,
  canAdmin: false,
  canAdminDbBackup: false,
  canUserManagement: false
};

const normalizePermissions = (permissions = {}) => {
  const normalized = { ...defaultPermissions };
  Object.keys(defaultPermissions).forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(permissions, key)) {
      normalized[key] = Boolean(permissions[key]);
    }
  });

  if (normalized.canAdmin) {
    normalized.canAdminDbBackup = true;
    normalized.canUserManagement = true;
  }

  if (!normalized.canRealEstate) {
    normalized.canRealEstateHub = false;
    normalized.canLeadGen = false;
    normalized.canCheckedAreas = false;
    normalized.canContacts = false;
    normalized.canPropertiesMap = false;
    normalized.canLandMap = false;
    normalized.canRawData = false;
    normalized.canMasterPortal = false;
  }

  if (!normalized.canMyTime) {
    normalized.canTimeTracking = false;
    normalized.canTimeTrackingOverview = false;
  }

  if (!normalized.canShortTermRentals) {
    normalized.canShortTermRentalsFinancials = false;
    normalized.canShortTermRentalsCleaning = false;
    normalized.canShortTermRentalsCleaningEdit = false;
  }

  if (!normalized.canShortTermRentalsCleaning) {
    normalized.canShortTermRentalsCleaningEdit = false;
  }

  return normalized;
};

const togglePermissionWithHierarchy = (currentPermissions, permissionKey) => {
  const nextPermissions = {
    ...currentPermissions,
    [permissionKey]: !Boolean(currentPermissions[permissionKey])
  };

  const childKeys = childPermissionMap[permissionKey] || [];
  if (childKeys.length > 0 && !nextPermissions[permissionKey]) {
    childKeys.forEach((childKey) => {
      nextPermissions[childKey] = false;
    });
  }

  const parentKey = parentPermissionMap[permissionKey];
  if (parentKey && nextPermissions[permissionKey]) {
    nextPermissions[parentKey] = true;
  }

  return normalizePermissions(nextPermissions);
};

const summarizePermissions = (permissions) => {
  const normalized = normalizePermissions(permissions || {});
  const mainSelected = permissionSections.filter((section) => normalized[section.key]).length;
  const subSelected = permissionSections.reduce((count, section) => {
    return count + section.children.filter((childKey) => normalized[childKey]).length;
  }, 0);
  return `${mainSelected} main pages, ${subSelected} subpages selected`;
};

const VisibilityModal = ({
  isOpen,
  title,
  permissions,
  defaultRoute,
  onDefaultRouteChange,
  onToggle,
  onClose,
  onApply
}) => {
  if (!isOpen) {
    return null;
  }

  return (
    <div className="um-modal-overlay" role="dialog" aria-modal="true">
      <div className="um-modal-card">
        <h3 className="um-modal-title">{title}</h3>

        <div className="um-modal-main-page" style={{ marginBottom: '16px' }}>
          <label style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', color: '#1e293b' }}>
            <Home size={16} /> Main Landing Page:
          </label>
          <select
            value={defaultRoute || '/'}
            onChange={(e) => onDefaultRouteChange(e.target.value)}
            className="um-select"
            style={{ width: '100%' }}
          >
            {Object.entries(groupedMainPageOptions).map(([groupLabel, options]) => (
              <optgroup key={groupLabel} label={groupLabel}>
                {options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        <p className="um-modal-hint">Main pages control access to their subpages. If a main page is turned off, all its subpages are automatically turned off.</p>

        <div className="um-modal-sections">
          {permissionSections.map((section) => {
            const mainChecked = Boolean(permissions[section.key]);
            return (
              <div key={section.key} className="um-section-block">
                <label className="um-main-checkbox">
                  <input
                    type="checkbox"
                    checked={mainChecked}
                    onChange={() => onToggle(section.key)}
                  />
                  <span>{section.label}</span>
                </label>

                {section.children.length > 0 && (
                  <div className="um-subpage-list">
                    {section.children.map((childKey) => {
                      const requiresCleaning = childKey === 'canShortTermRentalsCleaningEdit';
                      const disabled = !mainChecked || (requiresCleaning && !permissions.canShortTermRentalsCleaning);
                      return (
                        <label key={childKey} className="um-sub-checkbox">
                          <input
                            type="checkbox"
                            checked={Boolean(permissions[childKey])}
                            onChange={() => onToggle(childKey)}
                            disabled={disabled}
                          />
                          <span>{permissionLabels[childKey]}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="um-modal-actions">
          <button type="button" className="um-refresh" onClick={onClose}>Cancel</button>
          <button type="button" className="um-primary" onClick={onApply}>Apply Changes</button>
        </div>
      </div>
    </div>
  );
};

const UserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingUserId, setSavingUserId] = useState(null);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [deleteClicks, setDeleteClicks] = useState({});
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [visibilityEditor, setVisibilityEditor] = useState({
    isOpen: false,
    target: null,
    userId: null,
    permissions: { ...defaultPermissions },
    defaultRoute: '/'
  });

  const [newUser, setNewUser] = useState({
    username: '',
    displayName: '',
    password: '',
    isActive: true,
    permissions: { ...defaultPermissions },
    defaultRoute: '/'
  });

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/user-management/users`);
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to load users');
      }
      const nextUsers = (payload.users || []).map((user) => ({
        ...user,
        permissions: normalizePermissions(user.permissions || {}),
        defaultRoute: user.defaultRoute || '/'
      }));
      setUsers(nextUsers);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const openNewUserVisibilityModal = () => {
    setVisibilityEditor({
      isOpen: true,
      target: 'new',
      userId: null,
      permissions: normalizePermissions(newUser.permissions),
      defaultRoute: newUser.defaultRoute || '/'
    });
  };

  const openExistingUserVisibilityModal = (user) => {
    setVisibilityEditor({
      isOpen: true,
      target: 'existing',
      userId: user.id,
      permissions: normalizePermissions(user.permissions),
      defaultRoute: user.defaultRoute || '/'
    });
  };

  const handleVisibilityToggle = (permissionKey) => {
    setVisibilityEditor((prev) => ({
      ...prev,
      permissions: togglePermissionWithHierarchy(prev.permissions, permissionKey)
    }));
  };

  const handleEditorDefaultRouteChange = (nextRoute) => {
    setVisibilityEditor((prev) => ({
      ...prev,
      defaultRoute: nextRoute
    }));
  };

  const closeVisibilityModal = () => {
    setVisibilityEditor((prev) => ({ ...prev, isOpen: false }));
  };

  const applyVisibilityModal = () => {
    if (visibilityEditor.target === 'new') {
      setNewUser((prev) => ({
        ...prev,
        permissions: normalizePermissions(visibilityEditor.permissions),
        defaultRoute: visibilityEditor.defaultRoute
      }));
    }

    if (visibilityEditor.target === 'existing') {
      setUsers((prev) => prev.map((user) => {
        if (user.id !== visibilityEditor.userId) {
          return user;
        }

        return {
          ...user,
          permissions: normalizePermissions(visibilityEditor.permissions),
          defaultRoute: visibilityEditor.defaultRoute
        };
      }));
    }

    closeVisibilityModal();
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    setMessage('');
    setError('');

    if (!newUser.password.trim()) {
      setError('Password is required.');
      return;
    }

    if (newUser.password.trim().length < 5) {
      setError('Password must be at least 5 characters long.');
      return;
    }

    setLoading(true);
    try {
      const response = await authFetch(`${API_BASE}/api/user-management/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: newUser.username,
          displayName: newUser.displayName,
          password: newUser.password,
          isActive: newUser.isActive,
          permissions: normalizePermissions(newUser.permissions),
          defaultRoute: newUser.defaultRoute || '/'
        })
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to create user');
      }

      const createdLabel = payload.user.displayName || payload.user.username || `user-${payload.user.id}`;
      setMessage(`User ${createdLabel} created.`);
      setNewUser({
        username: '',
        displayName: '',
        password: '',
        isActive: true,
        permissions: { ...defaultPermissions },
        defaultRoute: '/'
      });
      await fetchUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveUser = async (user) => {
    setSavingUserId(user.id);
    setMessage('');
    setError('');

    try {
      const response = await authFetch(`${API_BASE}/api/user-management/users/${user.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          displayName: user.displayName,
          isActive: user.isActive,
          permissions: normalizePermissions(user.permissions),
          defaultRoute: user.defaultRoute || '/'
        })
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to save user');
      }

      setUsers((prev) => prev.map((current) => (
        current.id === payload.user.id
          ? {
              ...payload.user,
              permissions: normalizePermissions(payload.user.permissions),
              defaultRoute: payload.user.defaultRoute || '/'
            }
          : current
      )));
      const savedLabel = payload.user.displayName || payload.user.username || `user-${payload.user.id}`;
      setMessage(`Saved changes for ${savedLabel}.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingUserId(null);
    }
  };

  const handleDeleteUser = async (user) => {
    const nextCount = (deleteClicks[user.id] || 0) + 1;
    if (nextCount < 5) {
      setDeleteClicks((prev) => ({ ...prev, [user.id]: nextCount }));
      return;
    }

    setDeletingUserId(user.id);
    setMessage('');
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/user-management/users/${user.id}`, {
        method: 'DELETE'
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to delete user');
      }

      setUsers((prev) => prev.filter((current) => current.id !== user.id));
      setDeleteClicks((prev) => {
        const next = { ...prev };
        delete next[user.id];
        return next;
      });
      setMessage(payload.message || 'User deleted.');
    } catch (err) {
      setError(err.message);
      setDeleteClicks((prev) => ({ ...prev, [user.id]: 0 }));
    } finally {
      setDeletingUserId(null);
    }
  };

  return (
    <div className="user-management-page">
      <div className="um-header">
        <h2><Shield size={20} /> User Management</h2>
        <button type="button" className="um-refresh" onClick={fetchUsers} disabled={loading}>
          <RefreshCcw size={16} /> Refresh
        </button>
      </div>

      {message && <div className="um-message">{message}</div>}
      {error && <div className="um-error">{error}</div>}

      <form className="um-card" onSubmit={handleCreateUser}>
        <h3><UserPlus size={18} /> Add New User</h3>
        <div className="um-form-grid">
          <input
            type="text"
            placeholder="Username (optional)"
            value={newUser.username}
            onChange={(event) => setNewUser((prev) => ({ ...prev, username: event.target.value }))}
          />
          <input
            type="text"
            placeholder="Display Name"
            value={newUser.displayName}
            onChange={(event) => setNewUser((prev) => ({ ...prev, displayName: event.target.value }))}
          />
          <input
            type="password"
            placeholder="Password (min 5 chars)"
            value={newUser.password}
            onChange={(event) => setNewUser((prev) => ({ ...prev, password: event.target.value }))}
          />
          <div className="um-select-wrapper">
            <label className="um-label">Main Landing Page:</label>
            <select
              value={newUser.defaultRoute || '/'}
              onChange={(event) => setNewUser((prev) => ({ ...prev, defaultRoute: event.target.value }))}
              className="um-select"
            >
              {Object.entries(groupedMainPageOptions).map(([groupLabel, options]) => (
                <optgroup key={groupLabel} label={groupLabel}>
                  {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <label className="um-checkbox-inline" style={{ alignSelf: 'center' }}>
            <input
              type="checkbox"
              checked={newUser.isActive}
              onChange={(event) => setNewUser((prev) => ({ ...prev, isActive: event.target.checked }))}
            />
            Active
          </label>
        </div>

        <div className="um-visibility-row">
          <button
            type="button"
            className="um-secondary"
            onClick={openNewUserVisibilityModal}
          >
            <SlidersHorizontal size={16} /> Configure Visibility & Main Page
          </button>
          <span className="um-visibility-summary">{summarizePermissions(newUser.permissions)}</span>
        </div>

        <button type="submit" className="um-primary" disabled={loading}>
          <UserPlus size={16} /> Create User
        </button>
      </form>

      <div className="um-card">
        <h3>Existing Users</h3>
        <div className="um-users-table">
          <div className="um-users-head">
            <span>User</span>
            <span>Status</span>
            <span>Main Landing Page</span>
            <span>Visibility</span>
            <span>Action</span>
          </div>

          {users.map((user) => (
            <div key={user.id} className="um-user-row">
              <div>
                <input
                  type="text"
                  value={user.displayName || ''}
                  onChange={(event) => setUsers((prev) => prev.map((current) => (
                    current.id === user.id ? { ...current, displayName: event.target.value } : current
                  )))}
                  className="um-display-input"
                />
                <div className="um-username">{user.username ? `@${user.username}` : 'No username (password-only login)'}</div>
              </div>

              <label className="um-checkbox-inline">
                <input
                  type="checkbox"
                  checked={Boolean(user.isActive)}
                  onChange={(event) => setUsers((prev) => prev.map((current) => (
                    current.id === user.id ? { ...current, isActive: event.target.checked } : current
                  )))}
                />
                Active
              </label>

              <div>
                <select
                  value={user.defaultRoute || '/'}
                  onChange={(event) => {
                    const selectedRoute = event.target.value;
                    setUsers((prev) => prev.map((current) => (
                      current.id === user.id ? { ...current, defaultRoute: selectedRoute } : current
                    )));
                  }}
                  className="um-select"
                >
                  {Object.entries(groupedMainPageOptions).map(([groupLabel, options]) => (
                    <optgroup key={groupLabel} label={groupLabel}>
                      {options.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              <div className="um-visibility-cell">
                <span className="um-visibility-summary">{summarizePermissions(user.permissions)}</span>
                <button
                  type="button"
                  className="um-secondary"
                  onClick={() => openExistingUserVisibilityModal(user)}
                >
                  <SlidersHorizontal size={16} /> Edit Visibility
                </button>
              </div>

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="um-secondary"
                  onClick={() => handleSaveUser(user)}
                  disabled={savingUserId === user.id}
                >
                  <Save size={16} /> {savingUserId === user.id ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  className="um-danger"
                  onClick={() => handleDeleteUser(user)}
                  disabled={deletingUserId === user.id}
                >
                  <Trash2 size={16} />
                  {deletingUserId === user.id ? 'Deleting...' : `Delete (${deleteClicks[user.id] || 0}/5)`}
                </button>
              </div>
            </div>
          ))}

          {users.length === 0 && !loading && <div className="um-empty">No users found.</div>}
          {loading && <div className="um-empty">Loading users...</div>}
        </div>
      </div>

      <VisibilityModal
        isOpen={visibilityEditor.isOpen}
        title={visibilityEditor.target === 'new' ? 'Set New User Settings' : 'Edit User Visibility & Main Page'}
        permissions={visibilityEditor.permissions}
        defaultRoute={visibilityEditor.defaultRoute}
        onDefaultRouteChange={handleEditorDefaultRouteChange}
        onToggle={handleVisibilityToggle}
        onClose={closeVisibilityModal}
        onApply={applyVisibilityModal}
      />
    </div>
  );
};

export default UserManagementPage;
